/**
 * Learning rules shared by the store (UI guard) and the mock server (the check a real
 * backend must also make), like policy-rules.ts for Ayarlar:
 * - "Tahmin yok" below the minimum n, whatever value the server sent;
 * - Geri al only for reversible automatic changes; suppression and opt-out phrases are
 *   append-only and never reverted (critique H2);
 * - a revert needs a written reason, and the 1.5 s hold when it loosens (an A1 change
 *   reduced risk, so undoing it widens; so does restoring a pruned arm or a saturated cell);
 * - the learning batch: approving anything needs the reason and the batch hold, an H3 item
 *   also its own hold, H2 items only ship through their release PR, a rejection needs a
 *   reason code.
 */

import type {
  Estimate,
  FitLabelTask,
  LearningBatch,
  LearningBatchInput,
  LearningChange,
  LearningObjectType,
  LearningProposal,
  RegionEstimate,
  RevertInput,
} from './learning.js';
import { MIN_REASON_LENGTH, reasonText } from './policy-rules.js';

/** Capture-recapture estimates are reported only with at least this many recaptures. */
export const MIN_RECAPTURES = 7;

/** Append-only in the database: a revert is never offered and never accepted. */
export const NEVER_REVERTED: ReadonlySet<LearningObjectType> = new Set<LearningObjectType>([
  'suppression',
  'opt_out_phrase',
]);

/** A2 changes whose revert widens the search (restore an arm, reopen a saturated cell). */
const LOOSENING_A2: ReadonlySet<LearningObjectType> = new Set<LearningObjectType>(['arm_prune', 'saturation']);

/** A number the panel may show: a real value at or above the minimum n. */
export function hasEstimate(estimate: Estimate | null | undefined): estimate is Estimate & { value: number } {
  return (
    estimate !== null &&
    estimate !== undefined &&
    estimate.value !== null &&
    Number.isFinite(estimate.value) &&
    Number.isFinite(estimate.n) &&
    estimate.n >= estimate.minN &&
    estimate.n > 0
  );
}

/** The interval, only when both bounds exist and there is an estimate at all. */
export function intervalOf(estimate: Estimate): { low: number; high: number } | null {
  if (!hasEstimate(estimate) || estimate.low === null || estimate.high === null) return null;
  if (!Number.isFinite(estimate.low) || !Number.isFinite(estimate.high)) return null;
  return { low: estimate.low, high: estimate.high };
}

/** Region population estimates below m = 7 are only a lower bound ("alt sınır"). */
export function regionReportable(estimate: RegionEstimate): boolean {
  return estimate.reportable && estimate.m >= MIN_RECAPTURES && estimate.estimate !== null;
}

/** Distinct companies seen by either source: the lower bound shown when m < 7. */
export function regionLowerBound(estimate: RegionEstimate): number {
  return estimate.n1 + estimate.n2 - estimate.m;
}

/** Fit score and tier stay hidden on blind labelling screens. */
export function showsFitScore(task: FitLabelTask): boolean {
  return !task.blind;
}

export type LearningViolationCode =
  | 'not_found'
  | 'not_reversible'
  | 'already_reverted'
  | 'reason_required'
  | 'hold_required'
  | 'payload_changed'
  | 'nothing_decided'
  | 'release_only'
  | 'item_hold_required'
  | 'reject_reason_required'
  | 'unknown_item'
  | 'expired';

export interface LearningViolation {
  code: LearningViolationCode;
  message: string;
}

function violation(code: LearningViolationCode, message: string): LearningViolation {
  return { code, message };
}

/** Geri al is offered only here: an automatic, reversible change not yet reverted. */
export function canRevert(change: LearningChange): boolean {
  return (
    (change.changeClass === 'A1' || change.changeClass === 'A2') &&
    change.reversible === true &&
    change.revertedAt === null &&
    !NEVER_REVERTED.has(change.objectType)
  );
}

/** Undoing an A1 change (it reduced risk) or restoring a pruned arm or saturated cell widens. */
export function revertLoosens(change: LearningChange): boolean {
  return change.changeClass === 'A1' || LOOSENING_A2.has(change.objectType);
}

export function revertViolation(change: LearningChange | undefined, input: RevertInput): LearningViolation | null {
  if (!change) return violation('not_found', 'Bu değişiklik bulunamadı');
  if (change.revertedAt !== null) return violation('already_reverted', 'Bu değişiklik zaten geri alındı');
  if (!canRevert(change)) {
    return violation(
      'not_reversible',
      NEVER_REVERTED.has(change.objectType)
        ? 'Engel listesi ekleme amaçlıdır, geri alınmaz'
        : 'Bu değişiklik geri alınamaz',
    );
  }
  if (reasonText(input.reason).length < MIN_REASON_LENGTH) {
    return violation('reason_required', 'Geri almak için gerekçe yaz');
  }
  if (revertLoosens(change) && input.confirmedByHold !== true) {
    return violation('hold_required', 'Geri almak gevşetir: 1,5 sn basılı tut');
  }
  return null;
}

/** H2 items ship through their release PR; the panel never approves them. */
export function approvableInPanel(proposal: LearningProposal): boolean {
  return proposal.changeClass !== 'H2';
}

/** The batch rules; the server checks them again (and the payload hash). */
export function batchViolation(
  batch: LearningBatch | null,
  input: LearningBatchInput,
  now: number,
): LearningViolation | null {
  if (!batch || batch.batchId !== input.batchId) return violation('not_found', 'Öğrenme paketi bulunamadı');
  if (batch.payloadHash !== input.payloadHash) {
    return violation('payload_changed', 'Paket değişti · yeniden bak');
  }
  if (now >= batch.expiresAt) return violation('expired', 'Paketin süresi doldu');
  const byId = new Map(batch.proposals.map((proposal) => [proposal.proposalId, proposal]));
  const seen = new Set<string>();
  let decided = 0;
  let approving = false;
  for (const item of input.decisions) {
    const proposal = byId.get(item.proposalId);
    if (!proposal || seen.has(item.proposalId)) return violation('unknown_item', 'Pakette olmayan bir öneri var');
    seen.add(item.proposalId);
    if (item.decision === 'defer') continue;
    decided += 1;
    if (item.decision === 'reject') {
      if (item.reasonCode === null)
        return violation('reject_reason_required', `Reddetme gerekçesi seç: ${proposal.title}`);
      continue;
    }
    if (!approvableInPanel(proposal)) {
      return violation('release_only', `${proposal.title}: sürümle gelir, panelden onaylanmaz`);
    }
    if (proposal.changeClass === 'H3' && item.itemHoldConfirmed !== true) {
      return violation('item_hold_required', `${proposal.title}: T4, önce kendi düğmesini basılı tut`);
    }
    approving = true;
  }
  if (decided === 0) return violation('nothing_decided', 'Karar verilen öneri yok');
  if (approving) {
    if (reasonText(input.reason).length < MIN_REASON_LENGTH) {
      return violation('reason_required', 'Onaylamak için gerekçe yaz');
    }
    if (input.confirmedByHold !== true) return violation('hold_required', 'Onaylamak için 1,5 sn basılı tut');
  }
  return null;
}
